/**
 * A branding resource with URL and version for cache busting.
 */
export interface BrandingResource {
  /** URL to the resource */
  readonly resourceUrl: string;

  /** Version number for cache invalidation */
  readonly version: number;
}

/**
 * Known branding resource types returned by the API.
 */
export type BrandingResourceType =
  | 'Logo'
  | 'Favicon'
  | 'Favicon16'
  | 'Favicon32'
  | 'AppleTouchIcon'
  | 'Manifest';

/**
 * Tenant configuration returned by the /api/tenant-config endpoint.
 * Contains branding and configuration specific to the current tenant.
 */
export interface TenantConfig {
  /** Unique tenant identifier */
  readonly tenantId: string;

  /** Unique tenant identifier (e.g., "PL", "LH") */
  readonly tenantKey: string;

  /** Display name for the tenant organization (e.g., "Principled Leadership", "Lighthouse Institute") */
  readonly displayName: string;

  /** Environment name (e.g., "Prod", "Stage") */
  readonly environment: string;

  /** Application title displayed in browser tab and header */
  readonly appTitle: string;

  /** Base URL for branding assets in blob storage (e.g., "https://assets.frontlinesolutions.com/branding") */
  readonly brandingBaseUrl: string | null;

  /** Dictionary of branding resources keyed by resource type */
  readonly brandingResources: Record<string, BrandingResource>;

  /** Optional theme overrides for tenant-specific styling */
  readonly theme: TenantTheme | null;

  /** Base URL for the SSRS report server (e.g., "https://report.frontlinesolutions.com/Reporting/Report") */
  readonly reportServer: string | null;

  /** Tenant-specific report folder name (e.g., "LIGHTHOUSE", "LHSTAGE", "PL") */
  readonly reportFolder: string | null;

  /**
   * Map of registered feature-flag keys to their effective enabled state for the
   * resolved tenant. Piggybacked on tenant config so no separate fetch is needed.
   */
  readonly featureFlags?: Record<string, boolean>;
}

/**
 * Color palette following Material Design color roles.
 */
export interface TenantThemeColors {
  /** Primary brand color (hex) */
  readonly primary?: string;

  /** Text color on primary (hex) */
  readonly onPrimary?: string;

  /** Primary container color (hex) */
  readonly primaryContainer?: string;

  /** Text color on primary container (hex) */
  readonly onPrimaryContainer?: string;

  /** Secondary/accent color (hex) */
  readonly secondary?: string;

  /** Text color on secondary (hex) */
  readonly onSecondary?: string;

  /** Secondary container color (hex) */
  readonly secondaryContainer?: string;

  /** Text color on secondary container (hex) */
  readonly onSecondaryContainer?: string;

  /** Tertiary accent color (hex) */
  readonly tertiary?: string;

  /** Text color on tertiary (hex) */
  readonly onTertiary?: string;

  /** Tertiary container color (hex) */
  readonly tertiaryContainer?: string;

  /** Text color on tertiary container (hex) */
  readonly onTertiaryContainer?: string;

  /** Error/warning color (hex) */
  readonly error?: string;

  /** Text color on error (hex) */
  readonly onError?: string;
}

/**
 * Typography configuration for custom font families.
 */
export interface TenantThemeTypography {
  /** Font family for headings, navigation, titles (e.g., "'Merriweather', Georgia, serif") */
  readonly headingFontFamily?: string;

  /** Font family for body text, form labels (e.g., "'Montserrat', sans-serif") */
  readonly bodyFontFamily?: string;

  /** Google Fonts to load (e.g., ["Merriweather:wght@400;700", "Montserrat:wght@300;400;500"]) */
  readonly googleFonts?: string[];
}

/**
 * Theme customization options for tenant-specific styling.
 * Supports both nested structure (colors/typography) and flat colors for backward compatibility.
 */
export interface TenantTheme {
  /** Nested color configuration */
  readonly colors?: TenantThemeColors;

  /** Typography configuration */
  readonly typography?: TenantThemeTypography;

  // ---------------------------------------------------------------------------
  // Flat color properties for backward compatibility
  // ---------------------------------------------------------------------------

  /** Primary brand color (hex) */
  readonly primary?: string;

  /** Text color on primary (hex) */
  readonly onPrimary?: string;

  /** Primary container color (hex) */
  readonly primaryContainer?: string;

  /** Text color on primary container (hex) */
  readonly onPrimaryContainer?: string;

  /** Secondary/accent color (hex) */
  readonly secondary?: string;

  /** Text color on secondary (hex) */
  readonly onSecondary?: string;

  /** Secondary container color (hex) */
  readonly secondaryContainer?: string;

  /** Text color on secondary container (hex) */
  readonly onSecondaryContainer?: string;

  /** Tertiary accent color (hex) */
  readonly tertiary?: string;

  /** Text color on tertiary (hex) */
  readonly onTertiary?: string;

  /** Tertiary container color (hex) */
  readonly tertiaryContainer?: string;

  /** Text color on tertiary container (hex) */
  readonly onTertiaryContainer?: string;

  /** Error/warning color (hex) */
  readonly error?: string;

  /** Text color on error (hex) */
  readonly onError?: string;
}

/** Default logo URL used when no tenant logo is configured */
export const DEFAULT_LOGO_URL = 'assets/images/lighthouse.svg';

/**
 * Default fallback tenant configuration used when the API is unavailable.
 */
export const DEFAULT_TENANT_CONFIG: TenantConfig = {
  tenantId: '',
  tenantKey: 'LH',
  displayName: '360 Assessment',
  environment: 'Unknown',
  appTitle: '360 Assessment',
  brandingBaseUrl: null,
  brandingResources: {
    Logo: { resourceUrl: DEFAULT_LOGO_URL, version: 1 },
  },
  theme: null,
  reportServer: null,
  reportFolder: null,
};
